import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Send } from "lucide-react";

export default function RequestChat({ requestId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const listRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const { data } = await api.get(`/requests/${requestId}/messages`);
      setMessages(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchMessages();
    const iv = setInterval(fetchMessages, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setErr("");
    try {
      await api.post(`/requests/${requestId}/messages`, { text: text.trim() });
      setText("");
      await fetchMessages();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-[#0a0a0a] bg-white flex flex-col h-[460px]">
      <div className="px-4 py-3 border-b border-[#0a0a0a] flex items-center justify-between bg-[#fafafa]">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0a0a0a]">
          · chat
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8a8a]">
          {messages.length} messages
        </span>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="font-mono text-xs text-[#8a8a8a]">No messages yet.</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_user_id === user?.user_id;
            const isSystem = m.sender_role === "system";
            return (
              <div
                key={m.message_id}
                data-testid={`chat-msg-${m.message_id}`}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] px-3 py-2 border ${
                    isSystem
                      ? "border-[#00d4ff] bg-[#00d4ff]/5 text-[#0a0a0a]"
                      : mine
                      ? "border-[#0a0a0a] bg-[#0a0a0a] text-white"
                      : "border-[#0a0a0a] bg-white text-[#0a0a0a]"
                  }`}
                >
                  <div className={`font-mono text-[9px] uppercase tracking-[0.25em] mb-1 ${
                    isSystem ? "text-[#0a0a0a]" : mine ? "text-white/60" : "text-[#8a8a8a]"
                  }`}>
                    {m.sender_name || m.sender_role} · {new Date(m.created_at).toLocaleString()}
                  </div>
                  <div className="font-sans text-sm whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="border-t border-[#0a0a0a] p-3 flex gap-2 bg-[#fafafa]">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write a message…"
          className="flex-1 px-3 py-2 border border-[#0a0a0a] bg-white text-[#0a0a0a] font-sans text-sm focus:outline-none focus:border-[#ff006e]"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          data-testid="chat-send"
          className="px-4 py-2 bg-[#0a0a0a] text-white font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#ff006e] transition-colors disabled:opacity-50"
        >
          <Send size={14} />
        </button>
      </form>
      {err && <div className="px-3 pb-2 font-mono text-[10px] text-[#ff006e]">{err}</div>}
    </div>
  );
}
